import type { CsvRow, DataPoint } from '@/types/api'
import { parseCSVFile, validateOptimizationData, type CSVValidationOptions } from '@/utils/csv-parser'
import { convertRowsToCandles, createNormalisedJson, AmbiguousDateFormatError, type DateFormat } from '@/utils/candle-converter'
import { logger } from '@/utils/logger'

// Re-export for UI consumption
export { AmbiguousDateFormatError, type DateFormat } from '@/utils/candle-converter'

/**
 * Parse and validate data files for optimization
 * Supports JSON and CSV formats with data validation
 */
export interface ParsedDataFile {
  data: DataPoint[]
  normalizedJson: string
  normalizedFilename: string
  source: 'json' | 'csv'
  headers: string[]
  warnings: string[]
  rowCount: number
}

export interface ParseDataFileOptions {
  validation?: CSVValidationOptions
  dateFormat?: DateFormat
}

function isParseDataFileOptions(options: unknown): options is ParseDataFileOptions {
  return typeof options === 'object' && options !== null &&
    ('dateFormat' in options || 'validation' in options)
}

function normaliseFilename(filename: string): string {
  if (filename.toLowerCase().endsWith('.json')) {
    return filename
  }
  const base = filename.replace(/\.[^/.]+$/, '')
  return `${base}.json`
}

export async function parseDataFile(
  file: File,
  options?: CSVValidationOptions | ParseDataFileOptions
): Promise<ParsedDataFile> {
  // Extract validation options and dateFormat from the options union
  const validation = isParseDataFileOptions(options) ? options.validation : options
  const dateFormat = isParseDataFileOptions(options) ? options.dateFormat : undefined

  logger.info(`Parsing data file: ${file.name}`, {
    name: file.name,
    size: file.size,
    type: file.type
  })

  try {
    if (file.name.endsWith('.json')) {
      const text = await file.text()
      logger.debug(`File content length: ${text.length} characters`)

      try {
        let raw: unknown[]

        // First try standard JSON array format
        try {
          const parsed = JSON.parse(text)
          if (Array.isArray(parsed)) {
            raw = parsed
          } else {
            throw new Error('Not a JSON array')
          }
        } catch {
          // Fall back to NDJSON (newline-delimited JSON) format
          // Common in Databento exports where each line is a separate JSON object
          logger.debug('Standard JSON parse failed, attempting NDJSON format')
          const lines = text.split('\n').filter(line => line.trim())
          if (lines.length === 0) {
            throw new Error('JSON file is empty')
          }
          raw = lines.map((line, index) => {
            try {
              return JSON.parse(line)
            } catch (e) {
              throw new Error(`Invalid JSON on line ${index + 1}: ${e instanceof Error ? e.message : 'parse error'}`)
            }
          })
          logger.info(`Parsed ${raw.length} records from NDJSON format`)
        }

        const converterWarnings: string[] = []
        const candles = convertRowsToCandles(raw as Record<string, unknown>[], 0, dateFormat, converterWarnings)

        const validationResult = validateOptimizationData(candles)
        if (!validationResult.valid) {
          logger.error('JSON data validation failed', { errors: validationResult.errors })
          throw new Error(`Data validation failed: ${validationResult.errors.join('; ')}`)
        }

        const normalizedJson = createNormalisedJson(candles)
        const firstCandle = candles[0]
        const headers = firstCandle ? Object.keys(firstCandle) : ['timestamp', 'open', 'high', 'low', 'close', 'volume']

        logger.info(`Successfully parsed and validated JSON file: ${candles.length} items`)
        return {
          data: candles,
          normalizedJson,
          normalizedFilename: normaliseFilename(file.name),
          source: 'json',
          headers,
          warnings: converterWarnings,
          rowCount: candles.length,
        }
      } catch (error) {
        // Let AmbiguousDateFormatError propagate unmodified
        if (error instanceof AmbiguousDateFormatError) {
          throw error
        }
        const errorMessage = error instanceof Error ? error.message : 'Unknown error'
        logger.error('Failed to parse JSON file', { error: errorMessage })
        throw error instanceof Error ? error : new Error('Invalid JSON format in file')
      }
    } else if (file.name.endsWith('.csv')) {
      // Use secure CSV parser with validation
      const parseResult = await parseCSVFile(file, {
        requiredHeaders: ['timestamp'], // allow 'time' alias via csv-parser
        strictMode: false,
        allowEmptyRows: false,
        ...validation
      })

      // Log any warnings
      if (parseResult.warnings.length > 0) {
        logger.warn('CSV parsing warnings', { warnings: parseResult.warnings })
      }

      // Check for errors
      if (parseResult.errors.length > 0) {
        logger.error('CSV parsing errors', { errors: parseResult.errors })
        throw new Error(`CSV parsing failed: ${parseResult.errors.join('; ')}`)
      }

      const converterWarnings: string[] = []
      const candles = convertRowsToCandles(parseResult.data as CsvRow[], 0, dateFormat, converterWarnings)

      const validationResult = validateOptimizationData(candles)
      if (!validationResult.valid) {
        logger.error('CSV data validation failed', { errors: validationResult.errors })
        throw new Error(`Data validation failed: ${validationResult.errors.join('; ')}`)
      }

      const normalizedJson = createNormalisedJson(candles)
      const allWarnings = [...parseResult.warnings, ...converterWarnings]

      logger.info(`Successfully parsed and validated CSV file: ${candles.length} rows`)
      return {
        data: candles,
        normalizedJson,
        normalizedFilename: normaliseFilename(file.name),
        source: 'csv',
        headers: ['timestamp', 'open', 'high', 'low', 'close', 'volume'],
        warnings: allWarnings,
        rowCount: candles.length,
      }
    }

    const error = new Error('Unsupported file format. Please use .json or .csv files.')
    logger.error('Unsupported file format', { fileName: file.name, fileType: file.type })
    throw error
  } catch (error) {
    // Let AmbiguousDateFormatError propagate without wrapping
    if (error instanceof AmbiguousDateFormatError) {
      throw error
    }
    const errorMessage = error instanceof Error ? error.message : 'Unknown error'
    logger.error('File parsing failed', { error: errorMessage })
    throw error
  }
}
